import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import Card from '../../components/ui/Card'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { budgetAPI, categoriesAPI } from '../../services/api'
import useLanguage from '../../hooks/useLanguage'

const IncomePage = () => {
  const { t } = useTranslation('budget')
  const { locale } = useLanguage()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingIncome, setEditingIncome] = useState(null)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    amount: '',
    category_id: '',
    income_date: new Date().toISOString().split('T')[0],
    payment_method: '',
    source: '',
    notes: ''
  })

  const queryClient = useQueryClient()

  const { data: incomes, isLoading, error } = useQuery(
    ['incomes'],
    () => budgetAPI.getIncomes(),
    {
      onError: (error) => {
        console.error('Błąd podczas pobierania dochodów:', error)
      }
    }
  )

  const { data: categories } = useQuery(
    ['categories'],
    async () => {
      const response = await categoriesAPI.getCategories()
      return response.data.data.categories
    }
  )

  const createMutation = useMutation(
    async (incomeData) => {
      const response = await budgetAPI.createIncome(incomeData)
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['incomes'])
        queryClient.invalidateQueries('dashboard')
        setIsModalOpen(false)
        resetForm()
      }
    }
  )

  const updateMutation = useMutation(
    async ({ id, ...incomeData }) => {
      const response = await budgetAPI.updateIncome(id, incomeData)
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['incomes'])
        queryClient.invalidateQueries('dashboard')
        setIsModalOpen(false)
        setEditingIncome(null)
        resetForm()
      }
    }
  )

  const deleteMutation = useMutation(
    async (id) => {
      await budgetAPI.deleteIncome(id)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['incomes'])
        queryClient.invalidateQueries('dashboard')
      }
    }
  )

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      amount: '',
      category_id: '',
      income_date: new Date().toISOString().split('T')[0],
      payment_method: '',
      source: '',
      notes: ''
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const incomeData = {
      title: formData.title,
      amount: parseFloat(formData.amount),
      income_date: formData.income_date,
      ...(formData.description && { description: formData.description }),
      ...(formData.category_id && { category_id: formData.category_id }),
      ...(formData.payment_method && { payment_method: formData.payment_method }),
      ...(formData.source && { source: formData.source }),
      ...(formData.notes && { notes: formData.notes })
    }

    if (editingIncome) {
      updateMutation.mutate({ id: editingIncome.id, ...incomeData })
    } else {
      createMutation.mutate(incomeData)
    }
  }

  const handleEdit = (income) => {
    setEditingIncome(income)
    setFormData({
      title: income.title || '',
      description: income.description || '',
      amount: income.amount.toString(),
      category_id: income.category_id || '',
      income_date: income.income_date ? income.income_date.split('T')[0] : new Date().toISOString().split('T')[0],
      payment_method: income.payment_method || '',
      source: income.source || '',
      notes: income.notes || ''
    })
    setIsModalOpen(true)
  }

  const handleDelete = (id) => {
    if (window.confirm(t('incomes.confirmDelete'))) {
      deleteMutation.mutate(id)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'PLN'
    }).format(amount)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(locale)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <Card.Body>
            <div className="text-center py-12">
              <LoadingSpinner size="lg" text={t('incomes.loading')} />
            </div>
          </Card.Body>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Card>
          <Card.Body>
            <div className="text-center py-12">
              <div className="text-red-500">
                <p>{t('incomes.error')}</p>
                <p className="text-sm mt-2">{error.message}</p>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    )
  }

  const incomeList = incomes?.data?.incomes || []

  return (
    <div className="space-y-6">
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('incomes.subtitle')}
          </p>
        </div>
        <div className="mt-4 flex md:ml-4 md:mt-0">
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
          >
            {t('incomes.add')}
          </button>
        </div>
      </div>

      <Card>
        <Card.Body>
          {incomeList.length > 0 ? (
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-[var(--color-border)]">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                      {t('table.date')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                      {t('table.description')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                      {t('table.source')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                      {t('table.category')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                      {t('table.amount')}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                      {t('table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)]">
                  {incomeList.map((income) => (
                    <tr key={income.id} className="hover:bg-[var(--color-hover)]">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text)]">
                        {formatDate(income.income_date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-[var(--color-text)]">
                        <div className="font-medium">{income.title}</div>
                        {income.description && <div className="text-[var(--color-text-muted)] text-xs">{income.description}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-muted)]">
                        {income.source || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-muted)]">
                        {income.category?.name || t('common:noCategory')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-700">
                        +{formatCurrency(income.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(income)}
                          className="text-[#1f9f93] hover:text-[#167e75] mr-4"
                        >
                          {t('common:edit')}
                        </button>
                        <button
                          onClick={() => handleDelete(income.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          {t('common:delete')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-[var(--color-text-muted)]">{t('incomes.noIncomes')}</p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="mt-4 inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500"
              >
                {t('incomes.addFirst')}
              </button>
            </div>
          )}
        </Card.Body>
      </Card>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-[rgba(29,38,45,0.50)] transition-opacity" onClick={() => setIsModalOpen(false)}></div>

            <div className="relative transform overflow-hidden rounded-lg bg-[var(--color-surface)] px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <form onSubmit={handleSubmit}>
                <div>
                  <div className="mt-3 text-center sm:mt-0 sm:text-left">
                    <h3 className="text-base font-semibold leading-6 text-[var(--color-text)]">
                      {editingIncome ? t('incomes.editTitle') : t('incomes.addTitle')}
                    </h3>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label htmlFor="title" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('incomes.titleLabel')}
                        </label>
                        <input
                          type="text"
                          id="title"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] placeholder:text-[var(--color-text-faint)] focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                          placeholder={t('incomes.titlePlaceholder')}
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="amount" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('incomes.amountLabel')}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          id="amount"
                          value={formData.amount}
                          onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] placeholder:text-[var(--color-text-faint)] focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                          placeholder="0.00"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="income_date" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('incomes.dateLabel')}
                        </label>
                        <input
                          type="date"
                          id="income_date"
                          value={formData.income_date}
                          onChange={(e) => setFormData({ ...formData, income_date: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="source" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('incomes.sourceLabel')}
                        </label>
                        <input
                          type="text"
                          id="source"
                          value={formData.source}
                          onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] placeholder:text-[var(--color-text-faint)] focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                          placeholder={t('incomes.sourcePlaceholder')}
                        />
                      </div>

                      <div>
                        <label htmlFor="category_id" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('incomes.categoryLabel')}
                        </label>
                        <select
                          id="category_id"
                          value={formData.category_id}
                          onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                        >
                          <option value="">{t('incomes.selectCategory')}</option>
                          {categories && categories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="payment_method" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('incomes.paymentMethodLabel')}
                        </label>
                        <select
                          id="payment_method"
                          value={formData.payment_method}
                          onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                        >
                          <option value="">{t('incomes.selectMethod')}</option>
                          <option value="transfer">{t('common:paymentMethods.transfer')}</option>
                          <option value="cash">{t('common:paymentMethods.cash')}</option>
                          <option value="card">{t('common:paymentMethods.card')}</option>
                          <option value="mobile">{t('common:paymentMethods.mobile')}</option>
                          <option value="other">{t('common:paymentMethods.other')}</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="notes" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('incomes.notesLabel')}
                        </label>
                        <textarea
                          id="notes"
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          rows={2}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] placeholder:text-[var(--color-text-faint)] focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                          placeholder={t('incomes.notesPlaceholder')}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <button
                    type="submit"
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                    className="inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 sm:col-start-2 disabled:opacity-50"
                  >
                    {createMutation.isLoading || updateMutation.isLoading ? t('common:saving') : (editingIncome ? t('common:save') : t('common:add'))}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false)
                      setEditingIncome(null)
                      resetForm()
                    }}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-[var(--color-surface)] px-3 py-2 text-sm font-semibold text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] hover:bg-[var(--color-hover)] sm:col-start-1 sm:mt-0"
                  >
                    {t('common:cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default IncomePage
